import { useEffect, useMemo, useState } from 'react';
import { Button } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { EditAfeSourceModalView } from 'pages/packages/show/edit-afe-source-modal/edit-afe-source-modal-view';
import type { ExpenseAreaConfig } from '../types';
import { formatAfeLabel } from 'pages/afe-catalog/detail/utils';
import { afeCatalogLoadableAtom, afeQueryAtom } from './atoms';
import styles from './afe-selection-section.module.scss';

interface AfeSelectionSectionProps {
  area: ExpenseAreaConfig;
  onChange: (patch: Partial<ExpenseAreaConfig>) => void;
}

/**
 * Per-area fallback AFE. Properties in this area with no UWI-matched AFE of
 * their own use the AFE selected here for their Drilling and Completion CapEx.
 *
 * Reuses the shared Edit AFE Source modal rather than introducing a second AFE
 * picker; that component takes its candidates as props, and `label` replaces
 * its slot-oriented header with the area's name.
 * @param {AfeSelectionSectionProps} props - The area and change handler
 * @return {JSX.Element} The AFE selection section
 */
export function AfeSelectionSection({
  area,
  onChange,
}: AfeSelectionSectionProps): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const catalogState = useAtomValue(afeCatalogLoadableAtom);
  const setAfeQuery = useSetAtom(afeQueryAtom);

  // The catalog holds far more AFEs than the 100-row page cap, and the modal
  // filters only what it is given — so searching has to narrow server-side or
  // most AFEs would be unreachable. Debounced to avoid a request per keystroke.
  const debouncedAfeQuery = useMemo(
    () => debounce((text: string) => setAfeQuery(text), 350),
    [setAfeQuery]
  );

  useEffect(() => () => debouncedAfeQuery.cancel(), [debouncedAfeQuery]);

  const areaLabel = area.alias || area.areaName || area.unitName || 'New Area';
  const selected = area.fallbackAfe;

  return (
    <div className={styles.afeSelection}>
      <p className={styles.description}>
        Properties in this area that do not have a UWI-matched AFE will use the
        AFE selected here for their Drilling and Completion CapEx. Required —
        without one those properties have no cost source and go unvalued.
      </p>

      {selected ? (
        <div className={styles.row}>
          <dl className={styles.summary}>
            <div className={styles.summaryItem}>
              <dt className={styles.summaryLabel}>AFE</dt>
              <dd className={styles.summaryValue}>
                {selected.afeNumber ?? formatAfeLabel(selected.id)}
              </dd>
            </div>
            <div className={styles.summaryItem}>
              <dt className={styles.summaryLabel}>Type</dt>
              <dd className={styles.summaryValue}>{selected.afeType ?? '—'}</dd>
            </div>
            <div className={styles.summaryItem}>
              <dt className={styles.summaryLabel}>Well</dt>
              <dd className={styles.summaryValue}>
                {selected.wellName ?? selected.uwi ?? '—'}
              </dd>
            </div>
          </dl>
          {/* No Remove: the AFE is required, so clearing it could only produce
              an unsaveable scenario. It can be changed, not emptied. */}
          <div className={styles.actions}>
            <Button kind="ghost" size="sm" onClick={() => setModalOpen(true)}>
              Change AFE
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.emptyState}>
          <Button kind="tertiary" size="sm" onClick={() => setModalOpen(true)}>
            Select AFE
          </Button>
          {/* Save is gated on this, so the requirement has to be visible where
              it is unmet — the sibling percent-basis gate does the same rather
              than leaving the disabled button unexplained. */}
          <span className={styles.requiredHint}>
            Required before this scenario can be saved
          </span>
        </div>
      )}

      <EditAfeSourceModalView
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSearchChange={debouncedAfeQuery}
        label={`Area: ${areaLabel}`}
        // Unused for display, but the modal resets its selection only for a
        // truthy slot id, so give it a stable non-empty value.
        slotId={`expense-area-${area.id}`}
        currentAfeSourceId={
          area.fallbackAfeHeaderId
            ? String(area.fallbackAfeHeaderId)
            : undefined
        }
        data={catalogState.state === 'hasData' ? catalogState.data : null}
        isLoading={catalogState.state === 'loading'}
        error={catalogState.state === 'hasError' ? 'Failed to load AFEs' : null}
        onSave={(afeId, _href, _costs, afeWellName) => {
          const picked = (
            catalogState.state === 'hasData'
              ? catalogState.data.availableAfes
              : []
          ).find((a) => a.id === afeId);

          onChange({
            fallbackAfeHeaderId: Number(afeId),
            // Mirrors what the server will send back on reload, so the summary
            // renders immediately without a refetch.
            fallbackAfe: {
              id: Number(afeId),
              afeNumber:
                catalogState.state === 'hasData'
                  ? (catalogState.data.afeNumbersById[afeId] ?? null)
                  : null,
              afeType: picked?.afeType ?? null,
              wellName: afeWellName ?? picked?.well?.name ?? null,
              uwi: picked?.well?.id ?? null,
            },
          });
          setModalOpen(false);
        }}
      />
    </div>
  );
}
